import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../../app/store';

/**
 * The state/UT currently selected in the Platform Admin sidebar — the common,
 * module-wide "active state" that every per-state platform page reads from.
 * Persisted so it survives refreshes. Defaults to Rajasthan until the sidebar
 * loads the real list and the user picks one.
 */
export interface PlatformStateSel {
  code: string;
  name: string;
}

const KEY = 'chunaav.platformState';

function loadInitial(): PlatformStateSel {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PlatformStateSel>;
      if (p?.code) return { code: p.code, name: p.name ?? p.code };
    }
  } catch {
    /* ignore */
  }
  return { code: 'RJ', name: 'Rajasthan' };
}

const platformStateSlice = createSlice({
  name: 'platformState',
  initialState: loadInitial(),
  reducers: {
    setPlatformState(state, action: PayloadAction<PlatformStateSel>) {
      state.code = action.payload.code;
      state.name = action.payload.name;
      try {
        localStorage.setItem(KEY, JSON.stringify({ code: state.code, name: state.name }));
      } catch {
        /* ignore persistence errors */
      }
    },
  },
});

export const { setPlatformState } = platformStateSlice.actions;
export default platformStateSlice.reducer;

/** The active platform state `{ code, name }`, shared across every platform page. */
export const usePlatformState = (): PlatformStateSel =>
  useSelector((s: RootState) => s.platformState);
